/**
 * TipMi outbox → media-manager delivery phase on content intelligence.
 * When the Coop mirror is missing, admin-api may still return a stub with these fields
 * so the UI can show Opening / Delivery delayed instead of a dead end.
 */

export type CoopDeliveryStatus = 'none' | 'pending' | 'retrying' | 'delivered' | 'failed';

export function normalizeDeliveryStatus(
  raw: string | null | undefined,
): CoopDeliveryStatus | null {
  const v = (raw ?? '').trim().toLowerCase();
  if (
    v === 'none' ||
    v === 'pending' ||
    v === 'retrying' ||
    v === 'delivered' ||
    v === 'failed'
  ) {
    return v;
  }
  // Tolerate older / alternate spellings from stubs.
  if (v === 'retry' || v === 'retried') return 'retrying';
  if (v === 'dead') return 'failed';
  return null;
}

/** True while TipMi is still trying to open / deliver the Coop case mirror. */
export function isAwaitingCaseDelivery(
  deliveryStatus: string | null | undefined,
  caseStatus: string | null | undefined,
): boolean {
  const delivery = normalizeDeliveryStatus(deliveryStatus);
  if (delivery === 'pending' || delivery === 'retrying') return true;
  return (caseStatus ?? '').toLowerCase() === 'awaiting_delivery';
}

export function formatDeliveryLabel(
  deliveryStatus: string | null | undefined,
): string | null {
  switch (normalizeDeliveryStatus(deliveryStatus)) {
    case 'pending':
      return 'Coop · Opening';
    case 'retrying':
      return 'Coop · Delivery delayed';
    case 'failed':
      return 'Coop · Delivery failed';
    case 'delivered':
      return 'Coop · Delivered';
    default:
      return null;
  }
}

function parseInstant(raw: string | null | undefined): number | null {
  const v = (raw ?? '').trim();
  if (!v) return null;
  // LocalDateTime.toString() from admin-api is UTC wall time without a zone.
  const normalized =
    /(?:Z|[+-]\d{2}:\d{2})$/i.test(v) || v.includes('T') === false
      ? v
      : `${v}Z`;
  const ms = Date.parse(normalized.includes('T') ? normalized : v);
  return Number.isFinite(ms) ? ms : null;
}

/** Coarse duration for at-a-glance reading: seconds are noise on a 15 min backoff. */
function formatDurationCoarse(ms: number): string {
  const totalMinutes = Math.floor(Math.max(0, ms) / 60_000);
  if (totalMinutes < 1) return 'under a minute';
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function formatClockTime(ms: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(ms));
}

/** Strip `stage=` prefix from outbox last_stage for display. */
export function formatDeliveryStage(
  lastStage: string | null | undefined,
): string | null {
  const raw = (lastStage ?? '').trim();
  if (!raw) return null;
  const cleaned = raw.replace(/^stage=/i, '').trim();
  return cleaned || null;
}

/**
 * tipmi-api postponeDeep stores last_error like `stage=moderation_deep; in_flight`
 * when media-manager deep is still running (GPU wake / Gemma). That is a wait, not a failure.
 */
export function isDeepInFlightOutboxError(
  lastError: string | null | undefined,
  lastStage?: string | null | undefined,
): boolean {
  const err = (lastError ?? '').toLowerCase();
  const stage = (lastStage ?? '').toLowerCase();
  return (
    err.includes('in_flight') ||
    err.includes('deep_in_flight') ||
    err.includes('deep_dispatched') ||
    stage.includes('in_flight')
  );
}

/**
 * Collapse a Spring RestTemplate failure into something readable, e.g.
 * `500 Internal Server Error on POST request for ".../moderation-deep": "..."` → `500 on moderation-deep`.
 * The untouched message stays available for a tooltip.
 */
export function summarizeDeliveryError(
  raw: string | null | undefined,
  lastStage: string | null | undefined,
): string | null {
  const message = (raw ?? '').trim();
  if (!message) return null;

  if (isDeepInFlightOutboxError(message, lastStage)) {
    return 'Deep scan in progress';
  }

  const statusCode = /\b([1-5]\d{2})\b/.exec(message)?.[1] ?? null;
  const endpoint =
    /cases\/[^/\s"]+\/([a-z0-9-]+)/i.exec(message)?.[1] ??
    (lastStage ?? '').trim().replace(/^stage=/, '') ??
    null;

  if (statusCode && endpoint) return `${statusCode} on ${endpoint}`;
  if (statusCode) return `HTTP ${statusCode}`;
  if (message.length <= 80) return message;
  return `${message.slice(0, 77)}…`;
}

export type CoopScanPhase =
  | 'queued'
  | 'retry_waiting'
  | 'retrying_now'
  | 'delivered'
  | 'scanning'
  | 'failed'
  | 'none';

/** One label/value row in the Coop scan card, so timings stop competing on one line. */
export interface CoopScanDetail {
  label: string;
  value: string;
  /** Full text for a tooltip when `value` is abbreviated. */
  title?: string;
  tone?: 'default' | 'error';
}

export interface CoopScanStatusView {
  phase: CoopScanPhase;
  label: string;
  details: CoopScanDetail[];
  /** Flat one-line form, kept for compact surfaces (table cells, tooltips). */
  secondary: string | null;
  /** Footer caption describing where the state comes from. */
  hint: string;
  /** True while TipMi outbox is still actively trying to deliver. */
  isLive: boolean;
}

const OUTBOX_HINT = 'Outbox → media-manager';
const MIRROR_HINT = 'Media-manager mirror';

function toView(
  phase: CoopScanPhase,
  label: string,
  details: Array<CoopScanDetail | null>,
  hint: string,
  isLive: boolean,
): CoopScanStatusView {
  const rows = details.filter((d): d is CoopScanDetail => d != null);
  const secondary = rows.map((d) => `${d.label}: ${d.value}`).join(' · ') || null;
  return { phase, label, details: rows, secondary, hint, isLive };
}

/**
 * Dynamic Coop scan status for admin content pages.
 * Distinguishes queued vs in-flight deep (Scanning) vs waiting-for-retry vs
 * actively retrying, and surfaces last / next attempt times as separate rows.
 */
export function formatCoopScanStatus(opts: {
  deliveryStatus?: string | null | undefined;
  deliveryLastError?: string | null | undefined;
  deliveryLastStage?: string | null | undefined;
  deliveryAttemptCount?: number | null | undefined;
  deliveryLastAttemptAt?: string | null | undefined;
  deliveryNextAttemptAt?: string | null | undefined;
  /** Wall-clock now (ms); pass a ticking value for live countdown. */
  nowMs?: number | undefined;
  /** Optional overall elapsed since content moderation started. */
  elapsed?: string | null | undefined;
  /** When mirror exists but verdict is still provisional. */
  scanning?: boolean | undefined;
}): CoopScanStatusView {
  const delivery = normalizeDeliveryStatus(opts.deliveryStatus);
  const now = opts.nowMs ?? Date.now();
  const attemptCount = opts.deliveryAttemptCount ?? 0;
  const attemptRow: CoopScanDetail | null =
    attemptCount > 0 ? { label: 'Attempt', value: String(attemptCount) } : null;

  const lastMs = parseInstant(opts.deliveryLastAttemptAt);
  const nextMs = parseInstant(opts.deliveryNextAttemptAt);
  const lastRetryRow: CoopScanDetail | null =
    lastMs != null
      ? {
          label: 'Last retry',
          value: `${formatDurationCoarse(now - lastMs)} ago · ${formatClockTime(lastMs)}`,
        }
      : null;
  const nextDue = nextMs != null && nextMs <= now;
  const nextRetryRow: CoopScanDetail | null =
    nextMs == null
      ? null
      : nextDue
        ? { label: 'Next retry', value: 'now' }
        : {
            label: 'Next retry',
            value: `in ${formatDurationCoarse(nextMs - now)} · ${formatClockTime(nextMs)}`,
          };

  const fullError = opts.deliveryLastError?.trim() || null;
  const stageName = formatDeliveryStage(opts.deliveryLastStage);
  const stageRow: CoopScanDetail | null = stageName
    ? { label: 'Stage', value: stageName }
    : null;
  const lastActivityRow: CoopScanDetail | null =
    lastMs != null
      ? {
          label: 'Last activity',
          value: `${formatDurationCoarse(now - lastMs)} ago · ${formatClockTime(lastMs)}`,
        }
      : null;
  const errorRow: CoopScanDetail | null = fullError
    ? {
        label: 'Error',
        value: summarizeDeliveryError(fullError, opts.deliveryLastStage) ?? fullError,
        title: fullError,
        tone: 'error',
      }
    : null;

  if (delivery === 'failed') {
    return toView(
      'failed',
      'Coop · Delivery failed',
      [attemptRow, stageRow, lastRetryRow, errorRow],
      OUTBOX_HINT,
      false,
    );
  }

  if (delivery === 'delivered') {
    const deliveredRow: CoopScanDetail | null =
      lastMs != null
        ? { label: 'Delivered', value: `${formatDurationCoarse(now - lastMs)} ago` }
        : null;
    if (opts.scanning) {
      // Post-delivery mirror lag: deep usually finished during outbox, but a
      // provisional NO_ACTION can still appear briefly. Prefer last attempt as
      // scan start; `elapsed` is the whole moderation wait (often much longer).
      return toView(
        'scanning',
        'Coop · Scanning',
        [
          { label: 'Status', value: 'IN FLIGHT' },
          stageRow,
          {
            label: 'Deep scan',
            value:
              lastMs != null
                ? `in progress · ${formatDurationCoarse(now - lastMs)}`
                : 'in progress',
          },
          opts.elapsed ? { label: 'In moderation', value: opts.elapsed } : null,
          lastActivityRow,
        ],
        MIRROR_HINT,
        true,
      );
    }
    return toView(
      'delivered',
      'Coop · Delivered',
      [deliveredRow ?? { label: 'Delivered', value: 'waiting for media-manager mirror' }],
      MIRROR_HINT,
      false,
    );
  }

  if (delivery === 'pending' || delivery === 'retrying') {
    // tipmi-api postponeDeep while Gemma/GPU runs — not a delivery failure.
    if (isDeepInFlightOutboxError(opts.deliveryLastError, opts.deliveryLastStage)) {
      const scanStartedMs = lastMs ?? nextMs;
      return toView(
        'scanning',
        'Coop · Scanning',
        [
          { label: 'Status', value: 'IN FLIGHT' },
          stageRow,
          {
            label: 'Deep scan',
            value:
              scanStartedMs != null
                ? `in progress · ${formatDurationCoarse(now - scanStartedMs)}`
                : 'in progress (GPU may be waking)',
          },
          opts.elapsed ? { label: 'In moderation', value: opts.elapsed } : null,
          nextRetryRow ? { ...nextRetryRow, label: 'Next poll' } : null,
          lastActivityRow,
        ],
        OUTBOX_HINT,
        true,
      );
    }

    // tipmi-api runs PUT case + POST moderation-deep before markDelivered, so a
    // due PENDING row is the live deep-scan window — show Scanning, not Delivering.
    if (delivery === 'pending' && nextDue) {
      const scanStartedMs = lastMs ?? nextMs;
      return toView(
        'scanning',
        'Coop · Scanning',
        [
          { label: 'Status', value: 'IN FLIGHT' },
          stageRow,
          {
            label: 'Deep scan',
            value:
              scanStartedMs != null
                ? `in progress · ${formatDurationCoarse(now - scanStartedMs)}`
                : 'in progress',
          },
          opts.elapsed ? { label: 'In moderation', value: opts.elapsed } : null,
          lastActivityRow,
        ],
        OUTBOX_HINT,
        true,
      );
    }

    if (nextDue || (delivery === 'retrying' && nextMs == null && attemptCount > 0)) {
      return toView(
        'retrying_now',
        'Coop · Retrying now',
        [
          attemptRow,
          stageRow,
          lastRetryRow,
          nextRetryRow ?? { label: 'Next retry', value: 'now' },
          errorRow,
        ],
        OUTBOX_HINT,
        true,
      );
    }

    if (delivery === 'pending' && attemptCount === 0) {
      return toView(
        'queued',
        'Coop · Opening',
        [
          { label: 'Waiting', value: opts.elapsed ?? 'for first delivery' },
          nextRetryRow ? { ...nextRetryRow, label: 'First try' } : null,
        ],
        OUTBOX_HINT,
        true,
      );
    }

    return toView(
      'retry_waiting',
      'Coop · Delivery delayed',
      [attemptRow, stageRow, lastRetryRow, nextRetryRow, errorRow],
      OUTBOX_HINT,
      true,
    );
  }

  return toView('none', 'No Coop case', [], MIRROR_HINT, false);
}

/**
 * Rows for a Coop case that has already settled (approve / reject / escalate, or a
 * failed / timed-out scan). Same label/value shape as the delivery phases so the card
 * reads identically whichever state it is in.
 *
 * `decidedAt` is the TipMi ledger decision time: the intelligence payload carries no
 * Coop verdict timestamp, so the row is labelled "Decided" rather than "Approved".
 */
export function formatCoopSettledDetails(opts: {
  decidedAt?: string | null | undefined;
  /** Preformatted deep scan wall time, e.g. `21.5s`. */
  deepScanDuration?: string | null | undefined;
  caseStatus?: string | null | undefined;
  /** Populated for failed / timed-out scans. */
  verdictReason?: string | null | undefined;
  nowMs?: number | undefined;
}): CoopScanDetail[] {
  const now = opts.nowMs ?? Date.now();
  const decidedMs = parseInstant(opts.decidedAt);
  const caseStatus = (opts.caseStatus ?? '').trim();
  const reason = (opts.verdictReason ?? '').trim();

  const rows: Array<CoopScanDetail | null> = [
    decidedMs != null
      ? {
          label: 'Decided',
          value: `${formatDurationCoarse(now - decidedMs)} ago · ${formatClockTime(decidedMs)}`,
        }
      : null,
    opts.deepScanDuration ? { label: 'Deep scan', value: opts.deepScanDuration } : null,
    caseStatus ? { label: 'Case', value: caseStatus } : null,
    reason
      ? {
          label: 'Reason',
          value: reason.length <= 80 ? reason : `${reason.slice(0, 77)}…`,
          title: reason,
          tone: 'error',
        }
      : null,
  ];

  return rows.filter((d): d is CoopScanDetail => d != null);
}
